/*
 * EISCO manual viewer.
 *
 * Renders a PDF returned by the Cloud Run service inside the storefront page,
 * like an ebook. We use the pdf.js *core* library and paint pages onto our own
 * canvases, so there is no built-in toolbar and therefore no download / print /
 * save controls -- the only way to download is the button we add ourselves,
 * and only when the backend says the manual is downloadable (X-Downloadable).
 *
 * Reminder: this is a UX-level control. The PDF bytes reach the browser to be
 * displayed, so view-only is a deterrent, not hard DRM. This is an accepted,
 * documented trade-off.
 *
 * Wire-up: a root element with
 *   data-endpoint="https://<cloud-run-url>"   (required)
 * The token is taken from ?t=... in the URL (QR scan) or the lookup box.
 */
(function () {
  "use strict";

  var root = document.getElementById("manual-viewer");
  if (!root) return;

  var ENDPOINT = (root.getAttribute("data-endpoint") || "").replace(/\/+$/, "");
  var pagesEl = root.querySelector("[data-pages]");
  var statusEl = root.querySelector("[data-status]");
  var downloadBtn = root.querySelector("[data-download]");
  var form = root.querySelector("[data-lookup-form]");
  var input = root.querySelector("[data-lookup-input]");

  var currentBlob = null;
  var currentName = "manual.pdf";

  function setStatus(msg) {
    if (statusEl) statusEl.textContent = msg || "";
  }

  function tokenFromUrl() {
    var m = window.location.search.match(/[?&]t=([^&#]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  }

  function filenameFromDisposition(header) {
    if (!header) return "manual.pdf";
    var m = header.match(/filename="?([^"]+)"?/i);
    return m ? m[1] : "manual.pdf";
  }

  /* The backend returns errors as {"error": code, "message": friendly-text};
     show its message when present, otherwise fall back by status. */
  async function errorMessage(resp) {
    try {
      var data = await resp.json();
      if (data && data.message) return data.message;
    } catch (e) {
      /* not JSON; fall through */
    }
    if (resp.status === 404) return "No manual found for that code.";
    if (resp.status === 429) return "Too many requests. Please try again later.";
    return "Sorry, something went wrong (" + resp.status + ").";
  }

  function showDownload(name) {
    if (!downloadBtn) return;
    downloadBtn.hidden = false;
    downloadBtn.onclick = function () {
      if (!currentBlob) return;
      var url = URL.createObjectURL(currentBlob);
      var a = document.createElement("a");
      a.href = url;
      a.download = name || "manual.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    };
  }

  function hideDownload() {
    if (downloadBtn) downloadBtn.hidden = true;
  }

  function clearPages() {
    if (pagesEl) pagesEl.innerHTML = "";
  }

  // Render pages LAZILY. Only pages near the viewport hold a full-resolution
  // bitmap; off-screen ones are released. This keeps a long manual crisp on
  // mobile without tripping iOS Safari's canvas-memory ceiling, which would
  // otherwise silently blank-out or downscale later pages. Each on-screen page
  // is drawn at the device pixel ratio times a small oversample, so it stays
  // sharp both at fit-width and when pinch-zoomed.
  async function renderPdf(arrayBuffer) {
    if (typeof pdfjsLib === "undefined") {
      setStatus("Viewer failed to load. Please refresh.");
      return;
    }
    clearPages();
    var pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    var OVERSAMPLE = 1.5; // spare bitmap resolution for pinch-zoom headroom
    var MAX_SCALE = 5; // never blow a single page up past this
    var MAX_AREA = 16000000; // iOS Safari refuses canvases larger than ~16.7M px
    var containerWidth = pagesEl.clientWidth || 800;

    function drawScale(base) {
      var s = Math.min((containerWidth / base.width) * dpr * OVERSAMPLE, MAX_SCALE);
      var area = base.width * s * (base.height * s);
      if (area > MAX_AREA) s *= Math.sqrt(MAX_AREA / area); // clamp for iOS
      return s;
    }

    async function paint(canvas) {
      if (canvas.dataset.painted === "1") return;
      canvas.dataset.painted = "1";
      var page = await pdf.getPage(+canvas.dataset.page);
      var viewport = page.getViewport({ scale: drawScale(page.getViewport({ scale: 1 })) });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport: viewport }).promise;
    }

    function release(canvas) {
      if (canvas.dataset.painted !== "1") return;
      canvas.dataset.painted = "0";
      // Drop the bitmap to reclaim memory. The CSS box size is fixed below, so
      // the page keeps its place and simply re-paints when scrolled back.
      canvas.width = 0;
      canvas.height = 0;
    }

    var canvases = [];
    for (var n = 1; n <= pdf.numPages; n++) {
      var page = await pdf.getPage(n);
      var base = page.getViewport({ scale: 1 });
      var displayH = base.height * (containerWidth / base.width);

      var canvas = document.createElement("canvas");
      canvas.className = "manual-page";
      canvas.dataset.page = String(n);
      canvas.dataset.painted = "0";
      // Fix the display box now so scroll height is correct before paint and
      // stays stable when the bitmap is released.
      canvas.style.width = containerWidth + "px";
      canvas.style.height = Math.round(displayH) + "px";
      pagesEl.appendChild(canvas);
      canvases.push(canvas);
    }

    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) paint(e.target);
            else release(e.target);
          });
        },
        // Start painting ~1.5 screens ahead so scrolling rarely shows a blank.
        { root: null, rootMargin: "150% 0px" }
      );
      canvases.forEach(function (c) {
        io.observe(c);
      });
    } else {
      // No IntersectionObserver: just paint everything.
      for (var i = 0; i < canvases.length; i++) await paint(canvases[i]);
    }
  }

  async function load(token) {
    token = (token || "").trim();
    if (!token) {
      setStatus("Enter the code from your product label.");
      return;
    }
    if (!ENDPOINT) {
      setStatus("Viewer is not configured (missing endpoint).");
      return;
    }
    setStatus("Loading manual…");
    hideDownload();
    clearPages();

    var resp;
    try {
      resp = await fetch(ENDPOINT + "/manual?t=" + encodeURIComponent(token), {
        method: "GET",
        mode: "cors",
        credentials: "omit",
      });
    } catch (e) {
      setStatus("Could not reach the manual service. Check your connection.");
      return;
    }

    if (!resp.ok) return setStatus(await errorMessage(resp));

    var downloadable = (resp.headers.get("X-Downloadable") || "").toLowerCase() === "true";
    currentName = filenameFromDisposition(resp.headers.get("Content-Disposition"));
    currentBlob = await resp.blob();

    setStatus("");
    try {
      await renderPdf(await currentBlob.arrayBuffer());
    } catch (e) {
      setStatus("Could not display this manual.");
      return;
    }

    if (downloadable) showDownload(currentName);
    else hideDownload();
  }

  if (form) {
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      load(input ? input.value : "");
    });
  }

  var initial = tokenFromUrl();
  if (initial) {
    if (input) input.value = initial;
    load(initial);
  } else {
    setStatus("Scan a product QR code, or enter the code from the label above.");
  }
})();
